import { EmbeddedMessage, WhatsApp } from 'constants/messageMediums';
import { MessageMedium, WhatsAppSenderStatus } from 'modules/graphql/types';
import {
  AdvancedEcommerceTemplate,
  AdvancedLevel,
  AdvancedNewsletterTemplate,
  BasicEcommerceTemplate,
  BasicNewsletterTemplate,
  BasicStandardTemplate,
  BeginnerLevel,
  BlankTemplate,
  IntermediateLevel,
  OneColumnTemplate,
  SimpleTemplate,
  SingleItemTemplate,
} from 'ui/icons';
import { EditorDragAndDrop, EditorSideBySide, EditorWysiwyg } from 'ui/illustrations';
import { displayContent } from './content';
import { DragDropOption, FieldNames, GqlUpsertTemplateInvalidContentFailureTypes } from './types';

export const RAW_DATA_JSON_ROW = 'raw-data-json-row';

export const SPAM_REPORT_HREF = '/templates/spamReport';
export const PREVIEW_HTML_TEMPLATE_URL = '/templates/showHtml';
export const PREVIEW_IN_DEVICE_URL = '/templates/devicePreview';
export const RENDERED_DATA_URL = '/templates/saveJsonTestDataReturningRenderedData';
export const REVISION_HISTORY_URL = '/templates/history';

export const DATAFEED_DATA_URL = '/i/templates/templateFeeds';
export const DRAG_AND_DROP_TEMPLATES_URL = '/templates/builder-templates';
export const USER_DATA_URL = '/users/profiles/getUserData';

export const DISABLED_DESIGN_BUTTON_TEXT =
  'To add HTML content, choose a different message format. Otherwise, this message can only include JSON data.';

export const MAX_LENGTH_TEMPLATE_NAME = 100;
export const MAX_LENGTH_TEMPLATE_NAME_WHATSAPP = 255;

export const GSM7_SINGLE_MESSAGE_MAX_LENGTH = 160;
export const GSM7_MULTIPLE_MESSAGE_MAX_LENGTH = 153;
export const UCS2_SINGLE_MESSAGE_MAX_LENGTH = 70;
export const UCS2_MULTIPLE_MESSAGE_MAX_LENGTH = 67;

export const MESSAGE_MEDIUM_DATA_ARRAY_DELIMITER = ',';

export const TEMPLATE_EDITOR_QUERY_PARAMS = {
  campaignId: 'campaignId',
  experimentFlow: 'experimentFlow',
  locale: 'locale',
  placementId: 'placementId',
  templateId: 'templateId',
  view: 'view',
  workflowNodeFlow: 'workflowNodeFlow',
};

export const MESSAGE_MEDIUM_LABELS = {
  [MessageMedium.SMS]: 'SMS',
  [MessageMedium.Push]: 'Push',
  [MessageMedium.WebPush]: 'Web Push',
  [MessageMedium.InApp]: 'In App',
  [MessageMedium.Email]: 'Email',
  [EmbeddedMessage]: 'Embedded',
  [WhatsApp]: 'WhatsApp',
} as const;

export const CONTENT_EDITOR_OPTIONS = {
  DRAG_AND_DROP: {
    Image: EditorDragAndDrop,
    Level: BeginnerLevel,
    levelText: displayContent.strEditorLevelBeginnerText,
  },
  SIDE_BY_SIDE: {
    Image: EditorSideBySide,
    Level: AdvancedLevel,
    levelText: displayContent.strEditorLevelAdvancedText,
  },
  WYSIWYG: {
    Image: EditorWysiwyg,
    Level: IntermediateLevel,
    levelText: displayContent.strEditorLevelIntermediateText,
  },
};

// these static options filter the bee_builder_templates from the DB, provide order, and override
// the label (template_title) and altText (description)
export const DRAG_AND_DROP_OPTIONS: DragDropOption[] = [
  {
    id: 'BF-empty',
    label: 'Blank',
    altText: 'Blank template',
    svg: BlankTemplate,
  },
  {
    id: 'BF-basic-onecolumn',
    label: 'One Column',
    altText: 'Basic one-column template',
    svg: OneColumnTemplate,
  },
  {
    id: 'BF-basic-e-commerce',
    label: 'Basic ecommerce',
    altText: 'Basic e-commerce template',
    svg: BasicEcommerceTemplate,
  },
  {
    id: 'BF-ecommerce-template',
    label: 'Advanced ecommerce',
    altText: 'Advanced e-commerce template',
    svg: AdvancedEcommerceTemplate,
  },
  {
    id: 'BF-simple-template',
    label: 'Simple',
    altText: 'Simple one-column layout',
    svg: SimpleTemplate,
  },
  {
    id: 'BF-promo-template',
    label: 'Single item promotional',
    altText: 'A single-column promo layout, good for promoting a specific item',
    svg: SingleItemTemplate,
  },
  {
    id: 'BF-basic-newsletter',
    label: 'Basic newsletter',
    altText:
      'A one-column newsletter style layout, with images on the left-hand side, and text on the right',
    svg: BasicNewsletterTemplate,
  },
  {
    id: 'BF-newsletter-template',
    label: 'Advanced newsletter',
    altText: 'A more advanced newsletter layout',
    svg: AdvancedNewsletterTemplate,
  },
  {
    id: 'BF-basic-standard',
    label: 'Basic standard',
    altText: 'A one-column standard layout',
    svg: BasicStandardTemplate,
  },
];

export const SETTINGS_DISPLAY_CONTENT = {
  rawData: {
    labelText: 'Raw data (JSON)',
  },
  unsetText: 'Not set',
  addRawButton: 'Add raw data',
  removeRawButton: 'Remove raw data',
  rawDataTooltip: "Can't preview with raw data. Change message format to enable HTML.",
};

export const GQL_CONTENT_ERRORS: GqlUpsertTemplateInvalidContentFailureTypes[] = [
  GqlUpsertTemplateInvalidContentFailureTypes.AMBIGUOUS_EXPIRATION,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ACTION_BUTTON_URI,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_DEFAULT_ACTION,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_EMAIL,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_EXPIRATION,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_URI,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_MEDIA_URL,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_BUTTON_URL_PARAMS,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_BUTTON_URL_FORMAT,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SNIPPET_USAGE,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_HANDLEBAR_COUNT,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_HANDLEBAR_COMPLEXITY,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_FOOTER,
  GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_MESSAGE,
  GqlUpsertTemplateInvalidContentFailureTypes.EMOJI_IN_BUTTON_TEXT,
  GqlUpsertTemplateInvalidContentFailureTypes.WHATSAPP_EMOJI_LIMIT_EXCEEDED,
  GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_PHONE_NUMBER,
  GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_TEXT,
  GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_URL,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_MESSAGE_TYPE_ID,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ROKU_FONT_URI,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ROKU_HEX_VALUE,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SNIPPET_PARAMS,
  GqlUpsertTemplateInvalidContentFailureTypes.MISSING_LINK,
  GqlUpsertTemplateInvalidContentFailureTypes.MISSING_SNIPPETS,
  GqlUpsertTemplateInvalidContentFailureTypes.MISSING_UNSUBSCRIBE_URL,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_EXTENSION,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_SIZE,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_URL_FORMAT,
  GqlUpsertTemplateInvalidContentFailureTypes.UNRENDERABLE,
  GqlUpsertTemplateInvalidContentFailureTypes.UNRENDERABLE_WITH_INDEX,
];

export const GQL_CONTENT_WARNINGS: GqlUpsertTemplateInvalidContentFailureTypes[] = [
  GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_CONTENT,
  GqlUpsertTemplateInvalidContentFailureTypes.PREHEADER_TOO_LONG,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SUBJECT,
  GqlUpsertTemplateInvalidContentFailureTypes.MISSING_MESSAGE_OR_SILENT_PUSH,
  GqlUpsertTemplateInvalidContentFailureTypes.INVALID_REQUIRED_ROKU_CONTENT,
];

// has error messages that aren't dynamic
export const USER_FRIENDLY_FIELDS_ERROR_MESSAGE_MAPPER: Record<
  Extract<
    GqlUpsertTemplateInvalidContentFailureTypes,
    | GqlUpsertTemplateInvalidContentFailureTypes.AMBIGUOUS_EXPIRATION
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ACTION_BUTTON_URI
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_DEFAULT_ACTION
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_EXPIRATION
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_URI
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_MEDIA_URL
    | GqlUpsertTemplateInvalidContentFailureTypes.WHATSAPP_EMOJI_LIMIT_EXCEEDED
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_MESSAGE_TYPE_ID
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_REQUIRED_ROKU_CONTENT
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ROKU_FONT_URI
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ROKU_HEX_VALUE
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SUBJECT
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_BUTTON_URL_PARAMS
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_BUTTON_URL_FORMAT
    | GqlUpsertTemplateInvalidContentFailureTypes.EMOJI_IN_BUTTON_TEXT
    | GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_PHONE_NUMBER
    | GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_TEXT
    | GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_URL
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_FOOTER
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_EXTENSION
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_SIZE
    | GqlUpsertTemplateInvalidContentFailureTypes.INVALID_URL_FORMAT
    | GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_MESSAGE
    | GqlUpsertTemplateInvalidContentFailureTypes.MISSING_LINK
    | GqlUpsertTemplateInvalidContentFailureTypes.MISSING_UNSUBSCRIBE_URL
    | GqlUpsertTemplateInvalidContentFailureTypes.PREHEADER_TOO_LONG
  >,
  string
> = {
  [GqlUpsertTemplateInvalidContentFailureTypes.AMBIGUOUS_EXPIRATION]:
    'Expiration time cannot be in the past',
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ACTION_BUTTON_URI]:
    "Content in Action Buttons isn't formatted correctly",
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_DEFAULT_ACTION]:
    "Content in Open Action isn't formatted correctly",
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_EXPIRATION]:
    'Selected expiration time has an invalid date',
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_URI]:
    'Please provide a proper url in the format "https://somesite.com"',
  [GqlUpsertTemplateInvalidContentFailureTypes.WHATSAPP_EMOJI_LIMIT_EXCEEDED]:
    'Max of 10 emojis allowed in message body for marketing templates.',
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_MEDIA_URL]:
    "Content in Media URL isn't formatted correctly",
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_MESSAGE_TYPE_ID]:
    'Selected message type does not exist in your project',
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_EXTENSION]:
    'Supports JPEG and PNG only',
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_URL_FORMAT]: 'Invalid image URL',
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_BUTTON_URL_FORMAT]:
    'Invalid button URL',
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_SIZE]: 'Invalid image size',
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_REQUIRED_ROKU_CONTENT]:
    'Title, body, image or action button required to send campaign',
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ROKU_FONT_URI]:
    'Please provide a proper uri to your local roku font in the format "pkg://some/file/path.ttf"',
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ROKU_HEX_VALUE]:
    'Please provide a 3 or 6 character length hex value',
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SUBJECT]:
    'Subject is blank or has invalid content',
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_BUTTON_URL_PARAMS]:
    'Only one link parameter allowed in the Button Website URL',
  [GqlUpsertTemplateInvalidContentFailureTypes.EMOJI_IN_BUTTON_TEXT]: 'Invalid content in Button',
  [GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_PHONE_NUMBER]:
    'Phone number is required for a call phone button',
  [GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_TEXT]:
    'Button text is missing for button',
  [GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_URL]:
    'Website URL is required for a visit website button',
  [GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_FOOTER]:
    'Invalid content in Footer',
  [GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_MESSAGE]: 'Message required',
  [GqlUpsertTemplateInvalidContentFailureTypes.MISSING_LINK]:
    'In app messages are required to have a link',
  [GqlUpsertTemplateInvalidContentFailureTypes.MISSING_UNSUBSCRIBE_URL]:
    'Unsubscribe URL required to send campaign',
  [GqlUpsertTemplateInvalidContentFailureTypes.PREHEADER_TOO_LONG]:
    'Preheader must be less than 150 characters',
};

export const SUPPORTED_DESIGN_ERROR_FIELD_NAMES = [
  FieldNames.AMP_HTML,
  FieldNames.PLAIN_TEXT,
  FieldNames.HTML,
];

export const USER_FRIENDLY_FIELD_NAMES: Record<FieldNames, string> = {
  [FieldNames.ACTION_BUTTONS]: 'Action buttons',
  [FieldNames.AMP_HTML]: 'AMP HTML',
  [FieldNames.ATTACHMENT_URL]: 'Media URL',
  [FieldNames.ATTACHMENT_URL_ANDROID]: 'Media URL - Android',
  [FieldNames.BADGE]: 'Badge Count',
  [FieldNames.BCC_EMAILS]: 'BCC',
  [FieldNames.BODY]: 'Body',
  [FieldNames.BUTTONS]: 'Buttons',
  [FieldNames.CATEGORY]: 'Category',
  [FieldNames.CC_EMAILS]: 'CC',
  [FieldNames.CLICK_ACTION]: 'Click location',
  [FieldNames.DATA_FEED_ID]: 'Data feeds',
  [FieldNames.DEEPLINK]: 'Open Action',
  // @ts-expect-error there are multiple field names with the same value, causing a ts type error
  [FieldNames.DEFAULT_ACTION]: 'Open Action',
  [FieldNames.DISPLAY_SETTINGS]: 'Position',
  [FieldNames.EXPIRATION_DATE_TIME_ABSOLUTE]: 'Expiration time',
  [FieldNames.FROM_EMAIL]: 'From email',
  [FieldNames.FROM_NAME]: 'From name',
  [FieldNames.FROM_PHONE_NUMBER_ID]: 'Sender',
  [FieldNames.GOOGLE_ANALYTICS_CAMPAIGN_NAME]: 'Google Analytics parameter',
  [FieldNames.HEADER]: 'Header',
  [FieldNames.HTML]: 'HTML',
  [FieldNames.HTML_DATA_FEEDS]: 'HTML for data feeds',
  [FieldNames.ICON]: 'Icon',
  [FieldNames.IMAGE_URL]: 'Image URL',
  [FieldNames.INBOX_METADATA_ICON]: 'Mobile inbox title',
  [FieldNames.INBOX_METADATA_SUBTITLE]: 'Mobile inbox body',
  [FieldNames.INBOX_METADATA_TITLE]: 'Mobile inbox title',
  [FieldNames.INTERRUPTION_LEVEL]: 'Interruption level',
  [FieldNames.LINK_PARAMS]: 'Link parameters',
  [FieldNames.LINK_SHORTENING]: 'Shorten links',
  [FieldNames.MESSAGE]: 'Message',
  [FieldNames.FOOTER]: 'Footer',
  [FieldNames.MESSAGE_TYPE]: 'Message type',
  [FieldNames.PAYLOAD]: 'Raw data (JSON)',
  [FieldNames.PLAIN_TEXT]: 'Plain text',
  [FieldNames.PREHEADER_TEXT]: 'Preheader',
  [FieldNames.RELEVANCE_SCORE]: 'Releveance score',
  [FieldNames.REPLY_TO]: 'Reply to',
  [FieldNames.SOUND]: 'Custom sound',
  [FieldNames.SUBJECT]: 'Subject',
  [FieldNames.TITLE]: 'Title',
  [FieldNames.TRIGGER]: 'Preset animations',
  // @ts-expect-error there are multiple field names with the same value, causing a ts type error
  [FieldNames.WEB_DISPLAY_SETTINGS]: 'Position',
  [FieldNames.MISSING_MESSAGE_OR_SILENT_PUSH]: 'Message or deliver silently',
  [FieldNames.ROKU_TITLE]: 'Roku title',
  [FieldNames.ROKU_TITLE_FONT]: 'Roku title font',
  [FieldNames.ROKU_BUTTON_FONT]: 'Roku button font',
  [FieldNames.ROKU_BODY]: 'Roku body',
  [FieldNames.ROKU_BODY_FONT]: 'Roku body font',
  [FieldNames.ROKU_ACTION_BUTTONS]: 'Roku action buttons',
  [FieldNames.ROKU_BACKGROUND_COLOR]: 'Roku background color',
  [FieldNames.ROKU_FHD_IMAGE]: 'Roku image url',
};

export const GOOGLE_ANALYTICS_DISPLAY_CONTENT = {
  checkboxLabel: 'Enable Google Analytics for click tracking',
  helpText: 'This will append the following to all links for tracking purposes:',
  inputDefaultValue: 'campaign_{{campaignId}}',
  inputPrefix: 'utm_campaign',
  labelText: 'Google analytics',
};

export const LINK_PARAMETERS_DISPLAY_CONTENT = {
  addButton: 'Add link parameter',
  removeButton: 'Remove parameter',
  checkboxLabel: 'Add in additional query parameters to each URL. Parameters will be URL encoded.',
  labelText: 'Link parameters',
  keyPlaceholder: 'Key',
  valuePlaceholder: 'Value',
};

export const LINK_SHORTENING_DISPLAY_CONTENT = {
  checkboxLabel: 'Use short links to avoid message clipping',
  labelText: 'Link shortening',
};

export const WHATSAPP_SENDER_RESTRICTED_STATUSES = [
  WhatsAppSenderStatus.Restricted,
  WhatsAppSenderStatus.RateLimited,
];

export enum SpamClassification {
  NotSpam = 'NotSpam',
  Neutral = 'Neutral',
  Spam = 'Spam',
  StillProcessing = 'StillProcessing',
}
